import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { Button, FormGroup, Input } from "@cofano/cck";

import ApplicationDefinedStatusChanger, {
	ApplicationDefinedDialogProps,
	ApplicationDefinedStatus,
} from "view/statuses/components/changer/ApplicationDefinedStatusChanger";
import SailingReportStatusSelect, {
	SailingReportStatusSelectOption,
} from "view/sailingreports/components/selects/SailingReportStatusSelect";

import { CustomDialog } from "components/dialogs/Dialogs";

import { SailingReportStatus } from "__generated__/enums";
import { ChangeSailingReportStatusDocument } from "__generated__/view/sailingreports/graphql/sailingReport.mutations.generated";
import { GetSailingReportQuery } from "__generated__/view/sailingreports/graphql/sailingReport.queries.generated";

type SailingReportStatusObject = ApplicationDefinedStatus & { name: SailingReportStatus };

type SailingReportStatusChangeHistory = NonNullable<GetSailingReportQuery["sailingReport"]>["statusChanges"][number];

type SailingReportStatusChangerProps = {
	sailingReportId: number;
	currentStatus: SailingReportStatusObject;
	statusChanges: SailingReportStatusChangeHistory[];
	refetch: () => void;
	disabled?: boolean;
}

const SailingReportStatusChanger = ({
	sailingReportId,
	currentStatus,
	statusChanges,
	refetch,
	disabled,
}: SailingReportStatusChangerProps) => {

	return (
		<ApplicationDefinedStatusChanger
			currentStatus={currentStatus}
			disabled={disabled}
			initStatusChanges={async () => [ ...statusChanges ].reverse()}
			renderDialog={(dialogProps) => (
				<SailingReportStatusChangerDialog
					sailingReportId={sailingReportId}
					currentStatus={currentStatus}
					refetch={refetch}
					dialogProps={dialogProps}
				/>
			)}
			renderHistoryItem={(statusChange) => (
				<>
					<FormGroup label={intl.formatMessage({ id: "STATUS" })} overflow="hidden">
						<span>
							{intl.formatMessage({ id: `${statusChange.status.languageKeyPrefix}${statusChange.status.name}` })}
						</span>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
						<span>{statusChange.notes ?? "-"}</span>
					</FormGroup>
				</>
			)}
		/>
	);
};

const SailingReportStatusChangerDialog = ({
	sailingReportId,
	currentStatus,
	refetch,
	dialogProps,
}: ApplicationDefinedDialogProps<Omit<SailingReportStatusChangerProps, "statusChanges" | "disabled">>) => {

	const [ changeStatus, { loading } ] = useMutation(ChangeSailingReportStatusDocument);

	const [ selectedStatus, setSelectedStatus ] = useState<SailingReportStatusSelectOption | null>(null);
	const [ notes, setNotes ] = useState("");

	const reset = () => {
		setSelectedStatus(null);
		setNotes("");
	};

	const onSave = async () => {
		if (!selectedStatus) {
			return;
		}

		await changeStatus({
			variables: {
				sailingReportId,
				status: selectedStatus.name,
				notes: notes.trim() || null,
			},
		});

		dialogProps.onClose();
		reset();
		refetch();
	};

	return (
		<CustomDialog
			{...dialogProps}
			onClose={() => {
				dialogProps.onClose();
				reset();
			}}
			actions={(
				<Button
					onClick={onSave}
					loading={loading}
					disabled={loading || !selectedStatus}
					size="small"
				>
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}
		>
			<form>
				<FormGroup label={intl.formatMessage({ id: "STATUS" })} required overflow="hidden">
					<SailingReportStatusSelect
						value={selectedStatus}
						onChange={(option) => setSelectedStatus(option)}
						currentStatus={currentStatus.name}
					/>
				</FormGroup>

				{selectedStatus && (
					<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
						<Input value={notes} onChange={(event) => setNotes(event.target.value)} />
					</FormGroup>
				)}
			</form>
		</CustomDialog>
	);
};

export default SailingReportStatusChanger;
